import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import Link from '@mui/material/Link'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import { useTheme } from '@mui/material/styles'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface OssPackage {
  name: string
  version: string
  license: string
  repository: string | null
  author: string | null
  homepage: string | null
}

export function OssLicenses() {
  const theme = useTheme()
  const { t } = useTranslation()
  const [packages, setPackages] = useState<OssPackage[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetch('/oss-licenses.json')
      .then((res) => res.json())
      .then((data: OssPackage[]) => {
        setPackages(data)
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }, [])

  return (
    <Box
      display="flex"
      flexDirection="column"
      minHeight="100vh"
      bgcolor={theme.palette.background.page}
      padding={theme.spacing['3XL']}
      gap={theme.spacing.XL}
    >
      <Box>
        <Typography variant="h1" color={theme.palette.primary.main}>
          {t('common:oss.licenses')}
        </Typography>
        <Typography variant="body" color={theme.palette.text.secondary} sx={{ mt: 1, display: 'block' }}>
          {t('common:oss.licensesDescription')}
        </Typography>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" paddingTop={theme.spacing['5XL']}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} elevation={0}>
          <Table size="small" aria-label="open source licenses table">
            <TableHead>
              <TableRow>
                <TableCell>
                  <Typography variant="body-bold">{t('common:oss.package')}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body-bold">{t('common:oss.version')}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body-bold">{t('common:oss.license')}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body-bold">{t('common:oss.author')}</Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {packages.map((pkg) => (
                <TableRow key={`${pkg.name}@${pkg.version}`} hover>
                  <TableCell>
                    {pkg.homepage || pkg.repository ? (
                      <Link
                        href={pkg.homepage ?? pkg.repository ?? undefined}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {pkg.name}
                      </Link>
                    ) : (
                      <Typography variant="body">{pkg.name}</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body">{pkg.version}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body">{pkg.license}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body">{pkg.author ?? '—'}</Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  )
}
